import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api, UnauthorizedError } from './api';
import type { AuthUser } from './types';

interface AuthState {
  user: AuthUser | null;
  ready: boolean;                                    // initial /me probe complete
  signUp: (email: string, password: string) => Promise<void>;
  logIn:  (email: string, password: string) => Promise<void>;
  logOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.me()
      .then(u => { if (!cancelled) setUser(u); })
      .catch(err => {
        // A 401 here just means "not logged in" — it is not an error state.
        if (!(err instanceof UnauthorizedError)) console.error('auth probe failed', err);
      })
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, []);

  // If any api call 401s mid-session, the cookie has expired or been revoked.
  // Drop the user locally so the Gate re-renders the login screen.
  useEffect(() => {
    const onUnauth = () => setUser(null);
    window.addEventListener('cosy:unauth', onUnauth);
    return () => window.removeEventListener('cosy:unauth', onUnauth);
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    setUser(await api.signup(email, password));
  }, []);

  const logIn = useCallback(async (email: string, password: string) => {
    setUser(await api.login(email, password));
  }, []);

  const logOut = useCallback(async () => {
    try { await api.logout(); } catch { /* best-effort */ }
    setUser(null);
  }, []);

  return (
    <Ctx.Provider value={{ user, ready, signUp, logIn, logOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
